import { Component, Input, OnInit } from '@angular/core';
import { IGeneralInfoField, IProductCardForm } from '../../../product/models/models';
import { PHPermissions } from '../../models/PHPermissions';
import { FormGroup } from '@angular/forms';
import { ProductCard } from '../../../product/models/product-card';
import { SessionService } from '../../services/session.service';
import { ServiceFeatures } from '../../models/serviceFeatures';
import { Utils } from '../../services/utils.service';
import { ProductsService } from '../../services/products.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, takeUntil } from 'rxjs/operators';
import { Clipboard } from '@angular/cdk/clipboard';

import { SHARED_MODULES } from '../../../shared.';
import { HasAccessPipe } from '../../pipes/has-access.pipe';
import { ProductTypeTitlePipe } from '../../pipes/product-type-title.pipe';
import { RequiredFieldPipe } from '../../pipes/required-field.pipe';
import { ImageSelectComponent } from '../image-select/image-select.component';
import { LoaderComponent } from '../loader/loader.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';

@Component({
    selector: 'app-product-master-general-info',
    templateUrl: './product-master-general-info.component.html',
    styleUrls: ['./product-master-general-info.component.scss'],
	imports: [
		SHARED_MODULES,
		HasAccessPipe,
		ProductTypeTitlePipe,
		RequiredFieldPipe,
		ImageSelectComponent,
		LoaderComponent,
		PhIconComponent,
		PhIconWrapperComponent
	],
    standalone: true
})
export class ProductMasterGeneralInfoComponent implements OnInit {

	private _destroyCheckSubject: Subject<void> = new Subject<void>();

	public isArticleExists: boolean = false;
	public isArticleGenerating: boolean = false;
	public fields: Array<IGeneralInfoField>;
	public readonly permissions: typeof PHPermissions = PHPermissions;

	@Input() productForm: FormGroup<IProductCardForm>;
	@Input() product: ProductCard;
	@Input() showErrors: boolean = false;
	@Input() storeId: number;

	constructor(
		private clipboard: Clipboard,
		private snackBarService: SnackBarService,
		private sessionService: SessionService,
		private productsService: ProductsService
	) {}

	ngOnInit(): void {
		this.productForm.controls.name.enable();
		this.productForm.controls.article.enable();
		this.productForm.controls.description.enable();

		this.productForm.controls.article.valueChanges
			.pipe(debounceTime(500))
			.subscribe((value: string) => this.checkArticleExists(value));

		this.fields = [
			{ name: 'BARCODE', value: this.product.params.barcode },
			{ name: 'BRAND', value: this.product.params.brand_names },
			{ name: 'CATEGORY', value: this.product.params.category_paths }
		];

		if (this.sessionService.hasFeature(ServiceFeatures.Markers)) {
			this.fields.push({ name: 'CHARACTERISTIC', value: this.product.params.marker_paths });
		}

		if (!Utils.isNullOrUndefined(this.storeId)) {
			if (this.sessionService.hasFeature(ServiceFeatures.Suppliers)) {
				this.fields.push({ name: 'SUPPLIER', value: this.product.params.supplier_names });
			}

			if (this.sessionService.hasFeature(ServiceFeatures.ProductPrice)) {
				this.fields.push({ name: 'PRICE', value: String(this.product.params.price ?? '') });
			}

			if (this.sessionService.hasFeature(ServiceFeatures.ProductStock)) {
				this.fields.push({ name: 'STOCK', value: String(this.product.params.stock_qty ?? '') });
			}
		}
	}

	private checkArticleExists(value: string): void {
		this._destroyCheckSubject.next();
		if (!value) {
			this.isArticleExists = false;
			return;
		}

		this.productForm.controls.article.markAsPending();
		this.productsService
			.checkArticleUnique(value, { exclude_ids: String(this.product.id) })
			.pipe(
				takeUntil(this._destroyCheckSubject),
				catchError(() => of({ exists: false }))
			)
			.subscribe((response: { exists: boolean }) => {
				this.isArticleExists = response.exists;
				this.productForm.controls.article.updateValueAndValidity({ emitEvent: false });
			});
	}

	public copyDescription(): void {
		this.clipboard.copy(this.productForm.controls.description.value);
		this.snackBarService.setSnackBarNotification({
			messageText: 'COMMON.TEXT_WAS_COPIED'
		});
	}

	public generateArticle(): void {
		this.isArticleGenerating = true;
		this.productForm.controls.article.markAsPending();
		this.productsService.generateArticle().subscribe({
			next: (article: string) => {
				this.productForm.controls.article.markAsDirty();
				this.productForm.controls.article.setValue(article, { emitEvent: false });
				this.productForm.controls.article.updateValueAndValidity({ emitEvent: false });
				this.isArticleExists = false;
				this.isArticleGenerating = false;
			},
			error: () => {
				this.productForm.controls.article.updateValueAndValidity({ emitEvent: false });
				this.isArticleGenerating = false;
				this.snackBarService.setErrorNotification();
			}
		});
	}

	public onChangeImage(image: string): void {
		this.productForm.controls.photo.markAsDirty();
		this.productForm.controls.photo.setValue(image);
	}

	public wheelInput(event: WheelEvent): void {
		(event.target as HTMLElement).blur();
	}
}
